<template>
  <div>
    <gg-card>
      <gg-card-header>Mail Client Settings</gg-card-header>
      <gg-card-text>
        <gg-loading v-if="loading" :loading="true" type="setting-rows" :types="skeleton" />

        <div v-else-if="!rows.length" class="py-8 text-center">
          <p class="mb-0">Settings for this mailbox aren't available right now.</p>
        </div>

        <template v-else>
          <!-- Server and its ports on one row: cPanel reports a single set of
               ports per server, so splitting them across rows only made the
               list longer. -->
          <!-- The port sits right after the host it belongs to: in its own
               right-aligned column it drifted to the far edge on desktop, away
               from the value it describes. -->
          <v-row v-for="row in rows" :key="row.label" dense class="align-center">
            <v-col cols="12" sm="4" class="body-2">{{ row.label }}</v-col>
            <v-col cols="12" sm="8" class="d-flex align-center flex-wrap">
              <kvm-copyable :value="row.value" />
              <span v-if="row.ports" class="body-2 text--secondary ml-3">{{ row.ports }}</span>
            </v-col>
          </v-row>

          <v-row dense class="align-center">
            <v-col cols="12" sm="4" class="body-2">Password</v-col>
            <v-col cols="12" sm="8" class="body-2">Use your mailbox password</v-col>
          </v-row>

          <p class="body-2 mt-4 mb-0">
            Incoming and outgoing mail both require authentication.
          </p>
        </template>
      </gg-card-text>
    </gg-card>

    <gg-card class="mt-6">
      <gg-card-header>Email These Settings</gg-card-header>
      <gg-card-text>
        <p class="body-2">
          Send the settings for {{ mailbox.email }} to another address.
        </p>
        <!-- `align-end` rather than `align-center` because gg-text-field puts its
             label above the input: centring would line the button up with the
             label and the input together, not with the input. Same reason the
             dashboard's filter row uses it. The button is given the dense field's
             own 40px so the two ends of the row match. -->
        <v-form @submit.prevent="send">
          <v-row class="align-end">
            <v-col cols="12" md="6">
              <gg-text-field
                v-model="recipient"
                label="Email"
                name="Email"
                placeholder="you@example.com"
                hide-details="auto"
                v-validate="'required|email'"
                :error-messages="errors.collect('Email')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <gg-btn height="40" :loading="sending" :disabled="loading" @click="send">
                Send Instructions
              </gg-btn>
            </v-col>
          </v-row>
        </v-form>
      </gg-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import KvmCopyable from '@/views/services/hosting/kvm/KvmCopyable'

// Named rather than inline: a type ending in `@n` makes
// VSkeletonLoader return several root nodes, and GGLoading's `<transition>` takes
// only one.
const SKELETON = { 'setting-rows': 'list-item-two-line@3' }

export default {
  name: 'MailboxClientSetupView',
  components: {
    KvmCopyable,
  },
  props: {
    mailbox: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      skeleton: SKELETON,
      settings: null,
      loading: true,
      recipient: '',
      sending: false,
    }
  },
  computed: {
    /**
     * Only fields a live `Email::get_client_settings` response has actually
     * carried are read here, and any row whose value is missing is dropped, so
     * nothing can render a labelled blank. The shape is flat — one inbox
     * protocol, not a block per protocol — which is why there are no
     * IMAP / POP3 / Mobile tabs.
     *
     * Username comes from `account` rather than `inbox_username`:
     * ConfigurationModal.vue has shipped `account` for years and the two are
     * equal on every account seen so far.
     */
    rows () {
      const settings = this.settings
      if (!settings) return []

      // cPanel reports one inbox protocol, so the incoming row is labelled with
      // whichever it returns.
      const protocol = String(settings.inbox_service || '').toUpperCase() || 'Incoming'

      return [
        {
          label: `${protocol} Server`,
          value: settings.inbox_host,
          ports: this.portText(settings.inbox_port),
        },
        {
          label: 'SMTP Server',
          value: settings.smtp_host,
          ports: this.portText(settings.smtp_port),
        },
        {
          label: 'Username',
          value: settings.account,
          ports: '',
        },
      ].filter(row => row.value !== undefined && row.value !== null && row.value !== '')
        .map(row => ({ ...row, value: String(row.value) }))
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadClientSettings', 'sendClientSettings']),
    ...mapActions(['reportError', 'reportSuccess']),
    /**
     * Only the SSL port is shown: non-SSL connections are not supported
     * (AM-1104, Kaumil 2026-10-01), so the insecure port cPanel also reports
     * is deliberately dropped. A build that returns none leaves the cell empty
     * rather than showing an empty label.
     */
    portText (secure) {
      return secure ? `Port: ${secure} (SSL/TLS)` : ''
    },
    async load () {
      this.loading = true
      try {
        this.settings = await this.loadClientSettings({
          serviceId: this.mailbox.serviceId,
          account: this.mailbox.login,
        })
      } catch (e) {
        this.settings = null
        this.reportError('Unable to load mail client settings.')
      } finally {
        this.loading = false
      }
    },
    async send () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      this.sending = true
      try {
        await this.sendClientSettings({
          serviceId: this.mailbox.serviceId,
          account: this.mailbox.login,
          to: this.recipient,
        })
        this.recipient = ''
        this.$validator.reset()
        this.reportSuccess('Settings successfully sent.')
      } catch (e) {
        this.reportError(e)
      } finally {
        this.sending = false
      }
    },
  },
  created () {
    this.load()
  },
}
</script>
